/**
 * Pure press-and-hold timer (framework-free, unit-testable).
 *
 * The scene drives it with per-frame deltas and handles movement-based
 * cancellation; this class only owns the timing/progress + completion, so the
 * "hold to reset" and "cancel if released early" logic can be tested without
 * Phaser or the DOM.
 */
export interface HoldTick {
  progress: number; // 0..1
  completed: boolean;
}

export class HoldTimer {
  private elapsed = 0;
  private active = false;

  constructor(private readonly durationMs: number) {}

  start(): void {
    this.active = true;
    this.elapsed = 0;
  }

  cancel(): void {
    this.active = false;
    this.elapsed = 0;
  }

  isActive(): boolean {
    return this.active;
  }

  progress(): number {
    return this.active ? Math.min(1, this.elapsed / this.durationMs) : 0;
  }

  /** Advance by `delta` ms. Completes (and deactivates) when progress reaches 1. */
  update(delta: number): HoldTick {
    if (!this.active) return { progress: 0, completed: false };
    this.elapsed += delta;
    const progress = Math.min(1, this.elapsed / this.durationMs);
    const completed = progress >= 1;
    if (completed) this.active = false;
    return { progress, completed };
  }
}
